import { Directive, HostListener, inject, Input, OnDestroy, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';

import { ContentRectTracker } from './content-rect-tracker';
import { DEFAULT_EDGE_AUTO_SCROLL_CONFIG, type EdgeAutoScrollConfig, EdgeAutoScroller } from './edge-auto-scroller';
import { MarqueeOverlay } from './marquee-overlay';
import type {
  ContentRect,
  DragModifiers,
  DragSelectionDelegate,
  DragSelectionHitTester,
} from './drag-selection.types';
import { DRAG_SELECTION_HOST, type DragSelectionHost } from './drag-selection.host';

const DRAG_THRESHOLD_PX = 4;

@Directive({
  selector: '[tsDragSelection]',
  standalone: false,
})
export class DragSelectionDirective implements OnDestroy {
  @Input() public tsDragSelectionEnabled = false;
  @Input() public tsDragSelectionHitTester: DragSelectionHitTester<unknown> | null = null;
  @Input() public tsDragSelectionDelegate: DragSelectionDelegate<unknown> | null = null;

  private pointerDown = false;
  private dragStarted = false;
  private rafId: number | null = null;
  private scrollSub: Subscription | null = null;
  private lastModifiers: DragModifiers = { shift: false, ctrlOrMeta: false, alt: false };
  private latestRect: ContentRect | null = null;

  private readonly tracker = new ContentRectTracker();
  private autoScroller: EdgeAutoScroller | null = null;
  private marquee: MarqueeOverlay | null = null;

  private readonly renderer = inject(Renderer2);
  private readonly hostContext = inject<DragSelectionHost | null>(DRAG_SELECTION_HOST, { optional: true });

  public ngOnDestroy(): void {
    this.endStroke();
    this.marquee?.destroy();
    this.marquee = null;
  }

  public get isPointerDown(): boolean {
    return this.pointerDown;
  }

  public get isDragging(): boolean {
    return this.dragStarted;
  }

  public reset(): void {
    if (!this.pointerDown) return;
    if (this.dragStarted) this.tsDragSelectionDelegate?.dragSelectionCancelled();
    this.endStroke();
  }

  @HostListener('mousedown', ['$event'])
  public onMouseDown(event: MouseEvent): void {
    if (!this.canStart()) return;
    const viewport = this.hostContext?.getViewport();
    const content = this.hostContext?.getContent();
    if (!viewport || !content) return;
    if (event.button !== 0) return;

    if (!this.marquee) this.marquee = new MarqueeOverlay(this.renderer, content);
    if (!this.autoScroller) this.autoScroller = new EdgeAutoScroller(viewport, this.edgeAutoScrollConfig());

    this.tracker.attach(viewport);
    const anchor = this.tracker.start(event);
    if (!anchor) return;

    this.pointerDown = true;
    this.dragStarted = false;
    this.latestRect = null;
    this.lastModifiers = this.readModifiers(event);

    event.preventDefault();

    this.subscribeToScroll();
  }

  @HostListener('document:mousemove', ['$event'])
  public onDocumentMouseMove(event: MouseEvent): void {
    if (!this.pointerDown) return;
    this.lastModifiers = this.readModifiers(event);

    const rect = this.tracker.update(event);
    if (!rect) return;
    this.latestRect = rect;

    if (!this.dragStarted) {
      if (rect.width < DRAG_THRESHOLD_PX && rect.height < DRAG_THRESHOLD_PX) return;
      this.dragStarted = true;
      event.preventDefault();
      this.tsDragSelectionDelegate?.dragSelectionStarted(this.lastModifiers);
    }

    this.marquee?.show(rect);
    this.scheduleEmit();

    const pointer = this.tracker.getViewportPointer();
    if (pointer) this.autoScroller?.update(pointer.y);
  }

  @HostListener('document:mouseup')
  public onDocumentMouseUp(): void {
    if (!this.pointerDown) return;

    if (this.dragStarted) {
      const rect = this.tracker.getCurrentRect();
      const live = rect && this.tsDragSelectionHitTester ? this.tsDragSelectionHitTester.hitTest(rect) : [];
      this.tsDragSelectionDelegate?.dragSelectionCommitted(live, this.lastModifiers);
    }

    this.endStroke();
  }

  @HostListener('wheel', ['$event'])
  public onWheel(event: WheelEvent): void {
    if (!this.pointerDown) return;
    const viewport = this.hostContext?.getViewport();
    if (!viewport) return;
    event.preventDefault();
    viewport.scrollBy({ left: event.deltaX, top: event.deltaY, behavior: 'auto' });
  }

  @HostListener('document:keydown.escape')
  public onEscape(): void {
    if (!this.pointerDown) return;
    if (this.dragStarted) this.tsDragSelectionDelegate?.dragSelectionCancelled();
    this.endStroke();
  }

  private endStroke(): void {
    this.pointerDown = false;
    this.dragStarted = false;
    this.latestRect = null;
    this.cancelRaf();
    this.autoScroller?.stop();
    this.marquee?.hide();
    this.tracker.reset();
    this.scrollSub?.unsubscribe();
    this.scrollSub = null;
  }

  private subscribeToScroll(): void {
    this.scrollSub?.unsubscribe();
    const scroll$ = this.hostContext?.getScrollOffset$();
    if (!scroll$) return;
    this.scrollSub = scroll$.subscribe(() => {
      if (!this.pointerDown) return;
      const rect = this.tracker.getCurrentRect();
      if (!rect) return;
      this.latestRect = rect;
      if (this.dragStarted) {
        this.marquee?.show(rect);
        this.scheduleEmit();
      }
    });
  }

  private scheduleEmit(): void {
    if (this.rafId !== null) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      if (!this.pointerDown || !this.dragStarted) return;
      const rect = this.latestRect;
      if (!rect) return;
      const live = this.tsDragSelectionHitTester?.hitTest(rect) ?? [];
      this.tsDragSelectionDelegate?.dragSelectionUpdated(live, this.lastModifiers);
    });
  }

  private cancelRaf(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private canStart(): boolean {
    return this.tsDragSelectionEnabled && !!this.tsDragSelectionHitTester && !!this.tsDragSelectionDelegate;
  }

  private edgeAutoScrollConfig(): EdgeAutoScrollConfig {
    return this.hostContext?.getEdgeAutoScrollConfig?.() ?? DEFAULT_EDGE_AUTO_SCROLL_CONFIG;
  }

  private readModifiers(event: MouseEvent): DragModifiers {
    return {
      shift: event.shiftKey,
      ctrlOrMeta: event.ctrlKey || event.metaKey,
      alt: event.altKey,
    };
  }
}
